import { DocumentType, Media, UploadType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { ReactElement } from 'react';
import { getLocalizedString } from '@synergy/utils';
import {
    internalMediaSelector,
    removeUploadedFile,
    supplierMediaSelector,
    updateUploadedFileTitle,
    uploadFile,
} from '@synergy/data-access';

import { PaloAttachments } from '../../../common/components/palo/palo-attachments';
import { bestLocaleSelector } from '../../../common/selectors/locale-selectors';
import { noticeUploaded } from '../../../common/utils/upload-notice';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { usePaloPositionMedia } from '../../../common/components/palo/palo-position-media';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export interface PaloPurchaseOrderAttachmentsProps {
    /** The supplier's side is closed once the order is out. */
    supplierReadOnly?: boolean;
}

/**
 * The files of a purchase order, for the dock's "Anhänge" tab.
 *
 * Two sets, as on every document that talks to two sides: what goes to the
 * supplier with the remark, and what stays inside.
 */
export const PaloPurchaseOrderAttachments = ({ supplierReadOnly }: PaloPurchaseOrderAttachmentsProps): ReactElement => {
    const dispatch = useThunkDispatch();
    const supplier = useAppSelector(supplierMediaSelector);
    const internal = useAppSelector(internalMediaSelector);
    const downloadBaseUrl = useAppSelector(state => state.settings.mediaBaseUrl);
    const documentId = useAppSelector(state => state.purchaseOrders.entity?.id);
    const items = useAppSelector(state => state.purchaseOrders.entity?.purchaseOrderItems);
    const locale = useAppSelector(bestLocaleSelector);

    /**
     * What the positions carry, named after the position it belongs to. Adding
     * and removing stays at the position (key-user brief A3.3).
     */
    const positionMedia = usePaloPositionMedia();
    const positionFiles = (items ?? []).flatMap((item, index) =>
        positionMedia((item as { id?: number }).id, (item as { media?: Media[] }).media).map(media =>
            Media.fromPlainObject({
                ...media,
                // The number the table shows: the server lists the positions in
                // an order of its own, and the place in that list named the
                // wrong one.
                originalName: `Pos. ${(item as { position?: number }).position || index + 1} · ${getLocalizedString(
                    [locale],
                    item.name
                )} — ${media.originalName}`,
            })
        )
    );

    const drop =
        (existing: Media[], type?: UploadType) =>
        (files: File[], titles: string[] = []) => {
            const deduplicated = files
                .map((file, index) => ({ file, title: titles[index] }))
                .filter(
                    ({ file }) => !existing.some(media => media.originalName === file.name && media.size === file.size)
                );

            if (documentId && deduplicated.length > 0) {
                void noticeUploaded(
                    dispatch,
                    deduplicated.map(({ file, title }) =>
                        dispatch(uploadFile(DocumentType.PurchaseOrder, documentId, file, type, title))
                    )
                );
            }
        };

    const remove = (media: Media) => {
        if (documentId) {
            dispatch(removeUploadedFile(DocumentType.PurchaseOrder, documentId, media));
        }
    };

    const rename = (media: Media, title: string) => dispatch(updateUploadedFileTitle(media, title));

    const cancel = (media: Media) => media.cancel?.();

    return (
        <PaloAttachments
            groups={[
                {
                    key: 'supplier',
                    label: <FormattedMessage id="app.attachments.group.supplier" />,
                    files: supplier,
                    downloadBaseUrl,
                    // A document that has not been saved has nowhere to put a
                    // file yet, and says so rather than failing on the drop.
                    onDrop: supplierReadOnly || !documentId ? undefined : drop(supplier, UploadType.Supplier),
                    onRemove: supplierReadOnly ? undefined : remove,
                    onRename: supplierReadOnly ? undefined : rename,
                    onCancel: cancel,
                    readOnly: supplierReadOnly,
                    hint: <FormattedMessage id="app.upload.saveBeforeDrop" />,
                },
                {
                    key: 'internal',
                    label: <FormattedMessage id="app.attachments.group.internal" />,
                    files: internal,
                    downloadBaseUrl,
                    onDrop: documentId ? drop(internal) : undefined,
                    onRemove: remove,
                    onRename: rename,
                    onCancel: cancel,
                    positionFiles,
                    hint: <FormattedMessage id="app.upload.saveBeforeDrop" />,
                },
            ]}
        />
    );
};
